import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { useCancellationPreview } from '../hooks/useCancellationPreview';
import { formatCurrency } from '../../../lib/utils/formatters';

interface CancellationModalProps {
  bookingId: string | null;
  onClose: () => void;
  onConfirm: (bookingId: string, reason: string, notes?: string) => void;
  isSubmitting?: boolean;
  variant?: 'customer' | 'vendor';
}

const vendorCancellationReasons = [
  'Customer request',
  'Vendor unavailable',
  'No-show',
  'Technical issue',
  'Other',
];

const customerCancellationReasons = [
  'Change of plans',
  'Found alternative',
  'Schedule conflict',
  'Price concern',
  'Other',
];

/**
 * Modal for cancelling a booking with refund preview
 * Shows refund amount and policy before confirming
 */
export function CancellationModal({
  bookingId,
  onClose,
  onConfirm,
  isSubmitting,
  variant = 'vendor',
}: CancellationModalProps) {
  const cancellationReasons =
    variant === 'customer' ? customerCancellationReasons : vendorCancellationReasons;
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const { data: preview, isLoading: previewLoading } = useCancellationPreview(bookingId);

  const handleConfirm = () => {
    if (bookingId && reason) {
      onConfirm(bookingId, reason, notes || undefined);
    }
  };

  const handleClose = () => {
    setReason('');
    setNotes('');
    onClose();
  };

  return (
    <Dialog open={!!bookingId} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Cancel Booking</DialogTitle>
      <DialogContent>
        {previewLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : preview ? (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Refund Policy:</strong> {preview.policyMessage}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Customer will receive <strong>{formatCurrency(preview.refundAmount)}</strong> (
                {preview.refundPercentage}% refund)
              </Typography>
            </Alert>

            <TextField
              select
              fullWidth
              label="Cancellation Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              sx={{ mb: 2 }}
              SelectProps={{ native: true }}
            >
              <option value="">Select a reason</option>
              {cancellationReasons.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </TextField>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Additional Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional details about the cancellation..."
            />
          </>
        ) : (
          // If no preview available, show simple form without refund info
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Please provide a reason for cancelling this booking.
            </Typography>

            <TextField
              select
              fullWidth
              label="Cancellation Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              sx={{ mb: 2 }}
              SelectProps={{ native: true }}
            >
              <option value="">Select a reason</option>
              {cancellationReasons.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </TextField>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Additional Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional details about the cancellation..."
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Keep Booking
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={!reason || isSubmitting || previewLoading}
        >
          {isSubmitting ? <CircularProgress size={20} /> : 'Confirm Cancellation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
